/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useState, useContext, useEffect } from 'react'
import styles from './Dashboard.module.scss'
import {
	PieChart,
	Pie,
	Cell,
	ResponsiveContainer,
	Tooltip,
	Legend,
	BarChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Bar,
	Rectangle,
	LineChart,
	Line,
} from 'recharts'
import axios from 'axios'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faClipboardCheck,
	faFileAlt,
	faUser,
	faArrowRight,
	faLocationDot,
	faFile,
	faFileDownload,
	faCog,
	faBuilding,
} from '@fortawesome/free-solid-svg-icons'
import { faCalendar } from '@fortawesome/free-regular-svg-icons'
import { faTelegram } from '@fortawesome/free-brands-svg-icons'
import { Row, Col, Card, Button, Spinner, Image } from 'react-bootstrap'
import Filter from '../../components/Filter'
import { StateContext } from '../../StateContext'
import { useLocation, Link } from 'react-router-dom'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import dayjs from 'dayjs'
import EButton from '../../components/bootstrap-extended/EButton'
import BulkDataService from '../../services/BulkDataService'
import { saveAs } from 'file-saver'
import DashboardCard from '../../components/card/DashboardCard'

const colors = [
	'#FF5733',
	'#FFC300',
	'#FF9A3A',
	'#D4AC0D',
	'#77DD77',
	'#17A589',
	'#4A235A',
	'#6C3483',
	'#00FFFF',
	'#00BFFF',
	'#4169E1',
	'#1E90FF',
	'#20B2AA',
	'#2E8B57',
	'#008080',
	'#4682B4',
	'#8A2BE2',
	'#9932CC',
	'#7B68EE',
	'#BA55D3',
]

interface DataProps {
  job: number;
  jobseeker: number;
  application: number;
  ongoingVCF: string[];
  completedVCF: string[];
  applicationStatuses: { name: string; value: string; color: string }[];
  login: number;
  pmet: number;
  nonPmet: number;
  eventList: number;
  eventDetail: number;
  locationView: number;
  resumeBuilder: number;
  eAppointmentView: number;
  careerView: number;
  eventDetailView: number;
  eventListView: number;
  telegramView: number;
  resumeBuilderView: number;
  chart: any;
  kiosk: any;
  loginChart: any;
  flexiJobVacanciesView: number;
  virtualSoutheastView: number;
  virtualSouthwestView: number;
  virtualCentralView: number;
  virtualNortheastView: number;
  virtualNorthwestView: number;
  jobSoutheastView: number;
  jobSouthwestView: number;
  jobCentralView: number;
  jobNortheastView: number;
  jobNorthwestView: number;
  eventNetworkingView: number;
  eventCareerView: number;
  eventTrainingView: number;
  eventOpportunityView: number;
  eventAllView: number;
  xhLocationView: number;
  ntucHomeView: number;
  ntucReferalView: number;
  ntucContactView: number;
  ntucBenefitView: number;
  ntucReferalScan: number;
  ntucChatScan: number;
  ntucModalScan: number;
  ntucBannerScan: number;
  lhubCriticalView: number;
  lhubCriticalFormView: number;
  lhubFoodView: number;
  lhubFoodFormView: number;
  lhubHealthcareView: number;
  lhubHealthcareFormView: number;
  lhubIctView: number;
  lhubIctFormView: number;
  lhubKhrysalisView: number;
  lhubKhrysalisFormView: number;
  lhubSecurityView: number;
  lhubSecurityFormView: number;
  lhubSustainabilityView: number;
  lhubSustainabilityFormView: number;
  lhubWorkplaceView: number;
  lhubWorkplaceFormView: number;
  lhubBannerQr: number;
  lhubBannerView: number;
  lhubHomeView: number;
  lhubHomeFormView: number;
  aiccScan: number;
  aiccView: number;
}

function useQuery() {
	const { search } = useLocation()
	return React.useMemo(() => new URLSearchParams(search), [search])
}

interface DateCSSProps {
  classNames: {
    overlayWrapper: string;
    overlay: string;
  };
  children: React.ReactNode;
}

const CustomOverlay: React.FC<DateCSSProps> = ({
	classNames,
	children,
	...props
}) => {
	return (
		<div
			className={classNames.overlayWrapper}
			style={{ marginLeft: -85 }}
			{...props}
		>
			<div className={classNames.overlay}>{children}</div>
		</div>
	)
}

const Dashboard = () => {
	const query = useQuery()
	const { filterData } = useContext(StateContext)
	const [fetchStatus, setFetchStatus] = useState({
		isReady: false,
		err: false,
	})
	const initialState = {
		job: 0,
		jobseeker: 0,
		application: 0,
		ongoingVCF: [],
		completedVCF: [],
		applicationStatuses: [],
		login: 0,
		pmet: 0,
		nonPmet: 0,
		eventList: 0,
		eventDetail: 0,
		resumeBuilder: 0,
		locationView: 0,
		eAppointmentView: 0,
		careerView: 0,
		eventDetailView: 0,
		eventListView: 0,
		telegramView: 0,
		resumeBuilderView: 0,
		loginChart: [],
		chart: {},
		kiosk: [],
		flexiJobVacanciesView: 0,
		virtualSoutheastView: 0,
		virtualSouthwestView: 0,
		virtualCentralView: 0,
		virtualNortheastView: 0,
		virtualNorthwestView: 0,
		jobSoutheastView: 0,
		jobSouthwestView: 0,
		jobCentralView: 0,
		jobNortheastView: 0,
		jobNorthwestView: 0,
		eventNetworkingView: 0,
		eventCareerView: 0,
		eventTrainingView: 0,
		eventOpportunityView: 0,
		eventAllView: 0,
		xhLocationView: 0,
		ntucHomeView: 0,
		ntucReferalView: 0,
		ntucContactView: 0,
		ntucBenefitView: 0,
		ntucReferalScan: 0,
		ntucChatScan: 0,
		ntucModalScan: 0,
		ntucBannerScan: 0,
		lhubCriticalView: 0,
		lhubCriticalFormView: 0,
		lhubFoodView: 0,
		lhubFoodFormView: 0,
		lhubHealthcareView: 0,
		lhubHealthcareFormView: 0,
		lhubIctView: 0,
		lhubIctFormView: 0,
		lhubKhrysalisView: 0,
		lhubKhrysalisFormView: 0,
		lhubSecurityView: 0,
		lhubSecurityFormView: 0,
		lhubSustainabilityView: 0,
		lhubSustainabilityFormView: 0,
		lhubWorkplaceView: 0,
		lhubWorkplaceFormView: 0,
		lhubBannerQr: 0,
		lhubBannerView: 0,
		lhubHomeView: 0,
		lhubHomeFormView: 0,
		aiccScan: 0,
		aiccView: 0,
	}
	const [data, setData] = useState<DataProps>(initialState)
	const [startDate, setStartDate] = useState<string>('')
	const [endDate, setEndDate] = useState<string>('')
	const [kioskType, setKioskType] = useState<string[]>([])
	const [chartData, setChartData] = useState<any[]>([])
	const { overview } = filterData
  interface customizedLabelProp {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    percent: number;
  }
  const RADIAN = Math.PI / 180
  const renderCustomizedLabel = ({
  	cx,
  	cy,
  	midAngle,
  	innerRadius,
  	outerRadius,
  	percent,
  }: customizedLabelProp) => {
  	const radius = innerRadius + (outerRadius - innerRadius) * 2.2
  	const x = cx + radius * Math.cos(-midAngle * RADIAN)
  	const y = cy + radius * Math.sin(-midAngle * RADIAN)

  	return (
  		<text
  			x={x}
  			y={y}
  			fill="black"
  			style={{ fontSize: '12px', fontWeight: 'bold' }}
  			textAnchor={x > cx ? 'start' : 'end'}
  			dominantBaseline="central"
  		>
  			{`${(percent * 100).toFixed(0)}%`}
  		</text>
  	)
  }

  const renderCustomizedLabelPie = ({
  	cx,
  	cy,
  	midAngle,
  	innerRadius,
  	outerRadius,
  	percent,
  }: customizedLabelProp) => {
  	const radius = innerRadius + (outerRadius - innerRadius) * 1.1
  	const x = cx + radius * Math.cos(-midAngle * RADIAN)
  	const y = cy + radius * Math.sin(-midAngle * RADIAN)

  	return (
  		<text
  			x={x}
  			y={y}
  			fill="black"
  			style={{ fontSize: '12px', fontWeight: 'bold' }}
  			textAnchor={x > cx ? 'start' : 'end'}
  			dominantBaseline="central"
  		>
  			{`${(percent * 100).toFixed(0)}%`}
  		</text>
  	)
  }
  const startDateQuery = query.get('startDate')
  const endDateQuery = query.get('endDate')
  const filters = {
  	startDate: startDateQuery !== null && startDateQuery ? startDateQuery : '',
  	endDate: endDateQuery !== null && endDateQuery ? endDateQuery : '',
  }

  const handleExport = async () => {
  	try {
  		const input = {
  			startDate: filters.startDate,
  			endDate: filters.endDate,
  		}
  		const returnedData = await BulkDataService.returnKioskData(input)

  		if (returnedData !== null) {
  			const filename = `Kiosk E2I 2024 -${
  				kioskType.includes('Dawn') ? ' Dawn -' : ''
  			}${kioskType.includes('XH') ? ' XH -' : ''}${
  				kioskType.includes('Zann') ? ' Zann -' : ''
  			} (${
  				filters.startDate === ''
  					? 'Initial'
  					: dayjs(filters.startDate).format('DD/MMM/YYYY')
  			} - ${
  				filters.endDate === ''
  					? 'Current'
  					: dayjs(filters.endDate).format('DD/MMM/YYYY')
  			}).xlsx`
  			// Save on Client Side
  			saveAs(
  				new window.Blob([returnedData], {
  					type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
  				}),
  				filename
  			)
  		}
  	} catch (err) {
  		console.log(err)
  	}
  }
  // const completeDataByBranchAndDate = (data)=>{
  // 	data.forEach(entry => {
  // 		const kioskId = entry.kioskId
  // 		const kioskName = entry.KioskBranch.name
  // 		const date = entry.createdAt.split('T')[0] // Extract the date part from createdAt

  // 		// Create a key for kioskId, kioskName, and date
  // 		const key = kioskId + '-' + kioskName + '-' + date

  // 		// Add complete data if the key already exists, otherwise initialize with new data
  // 		if (completeDataByBranchAndDate[key]) {
  // 			completeDataByBranchAndDate[key].count++
  // 		} else {
  // 			completeDataByBranchAndDate[key] = {
  // 				kioskId: kioskId,
  // 				kioskName: kioskName,
  // 				date: date,
  // 				count: 1
  // 			}
  // 		}
  // 	})

  // 	// Convert the object into an array for easier usage and further processing
  // 	return Object.values(completeDataByBranchAndDate)
  // }

  const handleExportChart = () => {
  	const printContent = document.getElementById('printThis')?.innerHTML
  	const originalContent = document.body.innerHTML
  	document.body.innerHTML = printContent ?? ''
  	window.print()
  	document.body.innerHTML = originalContent
  }

  const fetchData = async () => {
  	try {
  		const res = await axios.get(
  			(process.env.REACT_APP_ROUTE_GET_OVERVIEW as string) +
          `?startDate=${filters.startDate}&endDate=${filters.endDate}`
  		)
  		const {
  			totalJobs: job,
  			totalJobseekers: jobseeker,
  			totalApplications: application,
  			ongoingVCFs: ongoingVCF,
  			completedVCFs: completedVCF,
  			statusUpdates,
  			chart,
  			kiosk,
  			...rest
  		} = res.data

  		const transformData = (data) => {
  			return Object.entries(data)
  				.filter(([key]) => key !== 'type')
  				.map(([key, value]) => {
  					const kioskBranch = kiosk.find((k) => k.id == key)
  					return {
  						name: kioskBranch.name,
  						value: value,
  					}
  				})
  		}

  		const loginObj = chart.find((c) => c.type === 'Kiosk Login')

  		const storedType = (await localStorage.getItem('kioskType')) ?? ''
  		const parsedType = JSON.parse(storedType)
  		if (Array.isArray(parsedType)) {
  			setKioskType(parsedType)
  		}

  		setData({
  			job,
  			jobseeker,
  			application,
  			ongoingVCF,
  			completedVCF,
  			applicationStatuses: [
  				{
  					name: 'Pending',
  					value: statusUpdates.pending,
  					color: '#FE9465',
  				},
  				{
  					name: 'Viewed',
  					value: statusUpdates.viewed,
  					color: '#8E6CFA',
  				},
  				{
  					name: 'Contact Unlocked',
  					value: statusUpdates.contactunlocked,
  					color: '#1CA8FB',
  				},
  				// {
  				// 	name: 'Successful',
  				// 	value: returnedData.statusUpdates.successful,
  				// 	color: '#2BCB5C',
  				// },
  				// {
  				// 	name: 'Unsuccessful',
  				// 	value: returnedData.statusUpdates.unsuccessful,
  				// 	color: '#FE6565',
  				// },
  			],
  			loginChart:
          parsedType.includes('Dawn') || parsedType.includes('Zann')
          	? transformData(loginObj)
          	: [],
  			kiosk,
  			...rest,
  		})
  		setFetchStatus({ isReady: true, err: false })
  		setStartDate(filters.startDate)
  		setEndDate(filters.endDate)

  		const convertChartData = () => {
  			return chart.map((item) => {
  				const result = { type: item.type }
  				for (const [key, value] of Object.entries(item)) {
  					if (key !== 'type') {
  						const kioskBranch = kiosk.find((k) => k.id === parseInt(key))
  						if (kioskBranch) {
  							result[kioskBranch.name] = value
  						}
  					}
  				}
  				return result
  			})
  		}

  		const chartData = convertChartData()
  		setChartData(chartData)
  	} catch (err) {
  		console.error(err)
  		setData(initialState)
  		setFetchStatus({ isReady: true, err: true })
  	}
  }

  useEffect(() => {
  	setFetchStatus({ isReady: false, err: false })

  	// const fetchDataBarChart = async () => {
  	// 	try {
  	// 		const res = await axios.get(
  	// 			(process.env.REACT_APP_ROUTE_GET_BAR_CHART_DATA as string) +
  	//         `?startDate=${filters.startDate}&endDate=${filters.endDate}`
  	// 		)
  	// 		const returnedDataChart = res.data
  	// 		const rslt = completeDataByBranchAndDate(returnedDataChart)
  	// 		console.log('rslt', rslt)
  	// 		setDataBar(rslt.map(entry => ({
  	// 				name: entry.date,
  	// 				[entry.kioskName]: entry.count,
  	// 				count: entry.count
  	// 			})))
  	// 		const kioskNames = rslt.map(entry => entry.kioskName).filter((value, index, self) => self.indexOf(value) === index)
  	// 		console.log('kioskNames', kioskNames)
  	// 		setDataBarKiosk(kioskNames)
  	// 		console.log('dataBar', dataBar)
  	// 	} catch (error) {
  	// 		console.error(error)
  	// 		setData(initialState)
  	// 		setFetchStatus({ isReady: true, err: true })
  	// 	}
  	// }
  	fetchData()
  	// fetchDataBarChart()
  }, [])

  const handleDateChange = (
  	startDateSelected: string,
  	endDateSelected: string
  ): void => {
  	window.location.href = `?startDate=${startDateSelected}&endDate=${endDateSelected}`
  }

  const handleClear = () => (window.location.href = '/')

  return (
  	<div>
  		<Row>
  			<Col>
  				<Filter />
  			</Col>
  		</Row>

  		{/* <br /> */}
  		{fetchStatus.isReady === false && <Spinner animation="border" />}
  		{fetchStatus.isReady && fetchStatus.err && (
  			<p className="mt-3 fw-bold">Sorry, something went wrong.</p>
  		)}
  		{fetchStatus.isReady && fetchStatus.err === false && (
  			<>
  				<Card className={styles.bigCardDashboard}>
  					<Card.Body>
  						<h5>
  							<strong>Kiosk Report</strong>
  						</h5>

  						<Row>
  							<Col style={{ display: 'flex', flexFlow: 'row wrap' }}>
  								<div className={styles.dateCont}>
  									<div className={styles.filterDateDiv}>
  										<Image
  											src="/img/cal.png"
  											alt="search"
  											width="15"
  											height="15"
  										/>
  										<DayPickerInput
  											formatDate={(str) => dayjs(str).format('YYYY-MM-DD')}
  											onDayChange={(day) =>
  												handleDateChange(
  													dayjs(day).format('YYYY-MM-DD'),
  													endDate
  												)
  											}
  											overlayComponent={CustomOverlay}
  											placeholder={'Start Date'}
  											value={startDate}
  										/>
  									</div>
  								</div>
  								<div className={`d-none d-sm-block ${styles.dateArrow}`}>
  									<FontAwesomeIcon
  										icon={faArrowRight}
  										size="xs"
  										color="#9FB1C7"
  									/>
  								</div>
  								<div className={styles.dateCont}>
  									<div className={styles.filterDateDiv}>
  										<Image
  											src="/img/cal.png"
  											alt="search"
  											width="15"
  											height="15"
  										/>
  										<DayPickerInput
  											formatDate={(str) => dayjs(str).format('YYYY-MM-DD')}
  											onDayChange={(day) =>
  												handleDateChange(
  													startDate,
  													dayjs(day).format('YYYY-MM-DD')
  												)
  											}
  											overlayComponent={CustomOverlay}
  											placeholder={'End Date'}
  											value={endDate}
  										/>
  									</div>
  								</div>
  								<div className={styles.vertical__align}>
  									<Button variant="link" onClick={handleClear}>
                      Clear
  									</Button>
  								</div>
  							</Col>
  							<Col lg={2} md={2}>
  								<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
  									<div onClick={handleExport}>
  										<EButton
  											buttonText="Export"
  											icon={<FontAwesomeIcon icon={faFileDownload} />}
  											isSubmitting={false}
  											className={styles.cusBtn}
  										/>
  									</div>
  								</div>
  							</Col>
  						</Row>
  						{(kioskType.includes('Dawn') || kioskType.includes('Zann')) && (
  							<Row>
  								<DashboardCard
  									title="Kiosk Login"
  									value={data.login}
  									icon={faUser}
  									link={`/kiosk-report?type=login&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  								/>
  							</Row>
  						)}
  						{(kioskType.includes('Dawn') || kioskType.includes('Zann')) && (
  							<Card className={styles.overviewCard}>
  								<Card.Body>
  									<Row>
  										<Col xs={8}>
  											<h5>
  												<strong>Qr Scan Report</strong>
  											</h5>
  										</Col>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Telegram PMET Scan"
  											value={data.pmet}
  											icon={faTelegram}
  											link={`/kiosk-report?type=telegram-pmet&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="Telegram Non-PMET Scan"
  											value={data.nonPmet}
  											icon={faTelegram}
  											link={`/kiosk-report?type=telegram-non-pmet&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="Resume Builder Scan"
  											value={data.resumeBuilder}
  											icon={faFile}
  											link={`/kiosk-report?type=resume-builder&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Event List Scan"
  											value={data.eventList}
  											icon={faCalendar}
  											link={`/kiosk-report?type=event-list&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="Event Details Scan"
  											value={data.eventDetail}
  											icon={faCalendar}
  											link={`/kiosk-report?type=event-detail&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="AICC Scan"
  											value={data.aiccScan}
  											icon={faFileAlt}
  											link={`/kiosk-report?type=aicc-scan&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  									</Row>
  									<Row className={styles.rowTitle}>
  										<Col xs={8}>
  											<h5>
  												<strong>Page View Report</strong>
  											</h5>
  										</Col>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="e2i Locations View"
  											value={data.locationView}
  											icon={faLocationDot}
  											link={`/kiosk-report?type=location-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="e2i E-Appointment View"
  											value={data.eAppointmentView}
  											icon={faBuilding}
  											link={`/kiosk-report?type=e-appointment-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="e2i Career Assessment Tool View"
  											value={data.careerView}
  											icon={faCog}
  											link={`/kiosk-report?type=career-assessment-tool-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Event List View"
  											value={data.eventListView}
  											icon={faCalendar}
  											link={`/kiosk-report?type=event-list-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="Event Detail View"
  											value={data.eventDetailView}
  											icon={faCalendar}
  											link={`/kiosk-report?type=event-detail-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="Telegram View"
  											value={data.telegramView}
  											icon={faTelegram}
  											link={`/kiosk-report?type=telegram-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Resume Builder View"
  											value={data.resumeBuilderView}
  											icon={faFile}
  											link={`/kiosk-report?type=resume-builder-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<DashboardCard
  											title="AICC View"
  											value={data.aiccView}
  											icon={faFileAlt}
  											link={`/kiosk-report?type=aicc-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  										/>
  										<Col></Col> {/* Adjust columns as needed */}
  									</Row>
  									{kioskType.includes('Zann') && (
  										<>
  											<Row className={styles.rowTitle}>
  												<Col xs={8}>
  													<h5>
  														<strong>NTUC Membership Report</strong>
  													</h5>
  												</Col>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Home View"
  													value={data.ntucHomeView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-home-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Referal View"
  													value={data.ntucReferalView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-referal-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Contact View"
  													value={data.ntucContactView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-contact-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Benefit View"
  													value={data.ntucBenefitView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-benefit-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Referal Scan"
  													value={data.ntucReferalScan}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-referal-scan&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Chat Scan"
  													value={data.ntucChatScan}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-chat-scan&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Modal Scan"
  													value={data.ntucModalScan}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-modal-scan&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Banner Scan"
  													value={data.ntucBannerScan}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=ntuc-banner-scan&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<Col></Col> {/* Adjust columns as needed */}
  											</Row>
  											<Row className={styles.rowTitle}>
  												<Col xs={8}>
  													<h5>
  														<strong>LHUB Report</strong>
  													</h5>
  												</Col>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Critical View"
  													value={data.lhubCriticalView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-critical-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Critical Form View"
  													value={data.lhubCriticalFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-critical-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Food View"
  													value={data.lhubFoodView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-food-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Food Form View"
  													value={data.lhubFoodFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-food-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Healthcare View"
  													value={data.lhubHealthcareView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-healthcare-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Healthcare Form View"
  													value={data.lhubHealthcareFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-healthcare-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="ICT View"
  													value={data.lhubIctView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-ict-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="ICT Form View"
  													value={data.lhubIctFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-ict-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Khrysalis View"
  													value={data.lhubKhrysalisView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-khrysalis-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Khrysalis Form View"
  													value={data.lhubKhrysalisFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-khrysalis-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Security View"
  													value={data.lhubSecurityView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-security-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Security Form View"
  													value={data.lhubSecurityFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-security-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Sustainability View"
  													value={data.lhubSustainabilityView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-sustainability-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Sustainability Form View"
  													value={data.lhubSustainabilityFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-sustainability-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Workplace View"
  													value={data.lhubWorkplaceView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-workplace-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Workplace Form View"
  													value={data.lhubWorkplaceFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-workplace-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Banner QR Scan"
  													value={data.lhubBannerQr}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-banner-qr&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Banner View"
  													value={data.lhubBannerView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-banner-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  											</Row>
  											<Row>
  												<DashboardCard
  													title="Home View"
  													value={data.lhubHomeView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-home-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<DashboardCard
  													title="Home Form View"
  													value={data.lhubHomeFormView}
  													icon={faFileAlt}
  													link={`/kiosk-report?type=lhub-home-form-view&page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												/>
  												<Col></Col>
  											</Row>
  										</>
  									)}
  								</Card.Body>
  							</Card>
  						)}
  						{kioskType.includes('XH') && (
  							<Card className={styles.overviewCard}>
  								<Card.Body>
  									<Row>
  										<Col xs={8}>
  											<h5>
  												<strong>Page View Report</strong>
  											</h5>
  										</Col>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Part Time/ Flexi Jobs Vacancies View"
  											value={data.flexiJobVacanciesView}
  											icon={faBuilding}
  										/>
  										<DashboardCard
  											title="e2i Career Centres and Job Centres View"
  											icon={faLocationDot}
  											value={data.xhLocationView}
  										/>
  										<DashboardCard
  											title="AICC View"
  											value={data.aiccView}
  											icon={faFileAlt}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="AICC Scan"
  											value={data.aiccScan}
  											icon={faFileAlt}
  										/>
  										<Col></Col>
  										<Col></Col>
  									</Row>
  									<Row className={styles.rowTitle}>
  										<Col xs={8}>
  											<h5>
  												<strong>Virtual Career Fairs</strong>
  											</h5>
  										</Col>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="South East View"
  											value={data.virtualSoutheastView}
  											icon={faFileAlt}
  										/>
  										<DashboardCard
  											title="South West View"
  											value={data.virtualSouthwestView}
  											icon={faFileAlt}
  										/>
  										<DashboardCard
  											title="Central View"
  											value={data.virtualCentralView}
  											icon={faFileAlt}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="North East View"
  											value={data.virtualNortheastView}
  											icon={faFileAlt}
  										/>
  										<DashboardCard
  											title="North West View"
  											value={data.virtualNorthwestView}
  											icon={faFileAlt}
  										/>
  										<Col></Col>
  									</Row>
  									<Row className={styles.rowTitle}>
  										<Col xs={8}>
  											<h5>
  												<strong>Jobs From Partners</strong>
  											</h5>
  										</Col>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="South East View"
  											value={data.jobSoutheastView}
  											icon={faFileAlt}
  										/>
  										<DashboardCard
  											title="South West View"
  											value={data.jobSouthwestView}
  											icon={faFileAlt}
  										/>
  										<DashboardCard
  											title="Central View"
  											value={data.jobCentralView}
  											icon={faFileAlt}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="North East View"
  											value={data.jobNortheastView}
  											icon={faFileAlt}
  										/>
  										<DashboardCard
  											title="North West View"
  											value={data.jobNorthwestView}
  											icon={faFileAlt}
  										/>
  										<Col></Col>
  									</Row>
  									<Row className={styles.rowTitle}>
  										<Col xs={8}>
  											<h5>
  												<strong>e2i Event Near Me</strong>
  											</h5>
  										</Col>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Networking Session View"
  											value={data.eventNetworkingView}
  											icon={faCalendar}
  										/>
  										<DashboardCard
  											title="Career Fairs View"
  											value={data.eventCareerView}
  											icon={faCalendar}
  										/>
  										<DashboardCard
  											title="Training Session"
  											value={data.eventTrainingView}
  											icon={faCalendar}
  										/>
  									</Row>
  									<Row>
  										<DashboardCard
  											title="Job Opportunity View"
  											value={data.eventOpportunityView}
  											icon={faCalendar}
  										/>
  										<DashboardCard
  											title="All Event"
  											value={data.eventAllView}
  											icon={faCalendar}
  										/>
  										<Col></Col>
  									</Row>
  								</Card.Body>
  							</Card>
  						)}
  						<Card className={styles.bigCardDashboard}>
  							<Card.Body>
  								<h5>
  									<strong>Findjobs App</strong>
  								</h5>
  								<Row>
  									<Col md={4} xs={12}>
  										<Card className={styles.overviewCard}>
  											<Card.Body>
  												<Link to="/joblist">
  													<Row>
  														<Col xs={4} className={styles.svgImgWrapper}>
  															<FontAwesomeIcon icon={faFileAlt} />
  														</Col>
  														<Col>
  															<div>
  																<h2>{data.job}</h2>
  																<small>Jobs</small>
  															</div>
  														</Col>
  													</Row>
  												</Link>
  											</Card.Body>
  										</Card>
  									</Col>
  									<Col md={4} xs={12}>
  										<Card className={styles.overviewCard}>
  											<Card.Body>
  												<Link
  													to={`/jobseekers?page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												>
  													<Row>
  														<Col xs={4} className={styles.svgImgWrapper}>
  															<FontAwesomeIcon icon={faUser} />
  														</Col>
  														<Col>
  															<div>
  																<h2>{data.jobseeker}</h2>
  																<small>Jobseekers</small>
  															</div>
  														</Col>
  													</Row>
  												</Link>
  											</Card.Body>
  										</Card>
  									</Col>
  									<Col md={4} xs={12}>
  										<Card className={styles.overviewCard}>
  											<Card.Body>
  												<Link
  													to={`/applications?page=1&startDate=${filters.startDate}&endDate=${filters.endDate}`}
  												>
  													<Row>
  														<Col xs={4} className={styles.svgImgWrapper}>
  															<FontAwesomeIcon icon={faClipboardCheck} />
  														</Col>
  														<Col>
  															<div>
  																<h2>{data.application}</h2>
  																<small>Applications</small>
  															</div>
  														</Col>
  													</Row>
  												</Link>
  											</Card.Body>
  										</Card>
  									</Col>
  								</Row>
  								{/* <br /> */}
  								<Row>
  									<Col>
  										<Card className={styles.bigCardDashboard}>
  											<Card.Body>
  												<Row>
  													<Col xs={8}>
  														<h5>
  															<strong>Status Updates</strong>
  														</h5>
  													</Col>
  													{/* <Col className={styles.viewAllWrapper}>
											<Link to="/">View All</Link>
										</Col> */}
  												</Row>
  												<Row>
  													<Col lg={overview ? 12 : 3} md={12}>
  														{/* <br /> */}
  														<div style={{ width: 250, height: 220 }}>
  															<ResponsiveContainer>
  																<PieChart width={225} height={200}>
  																	<Pie
  																		data={data.applicationStatuses}
  																		// cx="40%"
  																		// cy="50%"
  																		labelLine={true}
  																		label={renderCustomizedLabel}
  																		innerRadius={60}
  																		outerRadius={80}
  																		paddingAngle={4}
  																		fill="#8884d8"
  																		dataKey="value"
  																	>
  																		{data.applicationStatuses.map(
  																			(entry, index) => (
  																				<Cell
  																					key={`cell-${index}`}
  																					fill={entry.color}
  																				/>
  																			)
  																		)}
  																	</Pie>
  																</PieChart>
  															</ResponsiveContainer>
  														</div>
  													</Col>
  													<Col className={styles.status} lg={'auto'} md={12}>
  														{/* <p style={{marginLeft:'5px'}}>Click on the status to view the result</p> */}
  														{data.applicationStatuses.map(
  															(d: any, index: number) => {
  																return (
  																	<div
  																		className="d-flex align-items-center"
  																		key={index}
  																	>
  																		<span
  																			style={{
  																				height: '1rem',
  																				width: '1rem',
  																				backgroundColor: d.color,
  																				borderRadius: '50%',
  																				display: 'inline-block',
  																			}}
  																		/>
  																		<Button
  																			variant="light"
  																			style={{
  																				border: ' 1px solid #9FB1C7',
  																				color: '#9FB1C7',
  																				fontWeight: 'bold',
  																			}}
  																			size="sm"
  																		>
  																			{d.name}
  																		</Button>
  																	</div>
  																)
  															}
  														)}
  													</Col>
  												</Row>
  											</Card.Body>
  										</Card>
  									</Col>
  								</Row>
  							</Card.Body>
  						</Card>
  					</Card.Body>
  				</Card>
  			</>
  		)}
  	</div>
  )
}
export interface FilterTypes {
  overview: boolean;
  lmu: boolean;
  se: boolean;
}

export default Dashboard
